import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { X, PlusCircle, Edit, Trash2, QrCode, Copy } from 'lucide-react'; // Importando Copy
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getLocaisSessaoBySessao, deleteLocalSessao } from '../../services/api';
import LocalSessaoForm from './LocalSessaoForm';
import LocalSessaoQrCodeModal from './LocalSessaoQrCodeModal';
import ImportLocaisSessaoModal from './ImportLocaisSessaoModal'; // NOVO IMPORT

function GerenciarLocaisSessaoModal({ isOpen, onClose, sessaoId, sessaoNome }) {
    const [locaisSessao, setLocaisSessao] = useState([]);
    const [loadingLocais, setLoadingLocais] = useState(true);
    const [errorLocais, setErrorLocais] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [localSessaoSelecionado, setLocalSessaoSelecionado] = useState(null);

    const [showQrCodeModal, setShowQrCodeModal] = useState(false);
    const [selectedLocalSessaoForQrCode, setSelectedLocalSessaoForQrCode] = useState(null);
    
    // Estado para o novo modal de importação
    const [showImportModal, setShowImportModal] = useState(false);

    const fetchLocaisSessao = useCallback(async () => {
        if (!sessaoId) return;

        setLoadingLocais(true);
        setErrorLocais('');
        try {
            const data = await getLocaisSessaoBySessao(sessaoId);
            setLocaisSessao(data);
        } catch (err) {
            console.error('Erro ao buscar locais de sessão:', err);
            setErrorLocais(err.message || 'Não foi possível carregar os locais de sessão.');
            toast.error(err.message || 'Erro ao carregar locais de sessão.');
        } finally {
            setLoadingLocais(false);
        }
    }, [sessaoId]);

    useEffect(() => {
        if (isOpen && sessaoId) {
            fetchLocaisSessao();
        } else if (!isOpen) {
            setLocaisSessao([]);
            setErrorLocais('');
            setLoadingLocais(true);
            setShowForm(false);
            setLocalSessaoSelecionado(null);
            setSelectedLocalSessaoForQrCode(null);
            setShowQrCodeModal(false);
            setShowImportModal(false); // Limpar estado do modal de importação
        }
    }, [isOpen, sessaoId, fetchLocaisSessao]);

    if (!isOpen) {
        return null;
    }

    const handleEdit = (local) => {
        setLocalSessaoSelecionado(local);
        setShowForm(true);
    };

    const handleDelete = async (localId, localNome) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: `Você realmente deseja deletar o local "${localNome}"? Esta ação não pode ser desfeita e pode afetar check-ins existentes.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, deletar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteLocalSessao(localId);
                toast.success(`Local "${localNome}" deletado com sucesso!`);
                fetchLocaisSessao();
            } catch (err) {
                console.error('Erro ao deletar local de sessão:', err);
                toast.error(`Erro ao deletar local: ${err.message}`);
            }
        }
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setLocalSessaoSelecionado(null);
        fetchLocaisSessao();
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setLocalSessaoSelecionado(null);
    };

    const handleViewQrCode = (local) => {
        setSelectedLocalSessaoForQrCode(local); // Passa o objeto completo
        setShowQrCodeModal(true);
    };

    const handleCloseQrCodeModal = () => {
        setShowQrCodeModal(false);
        setSelectedLocalSessaoForQrCode(null);
    };

    const handleImportSuccess = () => {
        setShowImportModal(false);
        fetchLocaisSessao(); // Recarrega a lista após a importação
    };

    const LocaisSessaoListSkeleton = () => (
        <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center p-3 bg-muted/50 rounded-md">
                    <div className="ml-3 flex-grow space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-8 w-20" />
                </div>
            ))}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4">
            <Card className="w-full max-w-3xl relative max-h-[90vh] flex flex-col">
                <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"> {/* Adicionado transition-colors */}
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle className="mb-2">Gerenciar Salas para: {sessaoNome}</CardTitle> {/* Adicionado mb-2 */}
                    <CardDescription className="leading-relaxed">Defina as salas ou locais específicos onde o check-in pode ser realizado para esta sessão.</CardDescription> {/* Adicionado leading-relaxed */}
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto">
                    {showForm ? (
                        <LocalSessaoForm
                            localSessao={localSessaoSelecionado}
                            sessaoId={sessaoId}
                            onSuccess={handleFormSuccess}
                            onCancel={handleFormCancel}
                        />
                    ) : (
                        <>
                            <div className="flex flex-col sm:flex-row justify-end gap-2 mb-4">
                                <Button variant="outline" onClick={() => setShowImportModal(true)} className="w-full sm:w-auto">
                                    <Copy className="mr-2 h-4 w-4" /> Importar Locais
                                </Button>
                                <Button onClick={() => { setLocalSessaoSelecionado(null); setShowForm(true); }} className="w-full sm:w-auto">
                                    <PlusCircle className="mr-2 h-4 w-4" /> Adicionar Nova Sala
                                </Button>
                            </div>
                            {loadingLocais ? (
                                <LocaisSessaoListSkeleton />
                            ) : errorLocais ? (
                                <p className="text-destructive text-center py-8">{errorLocais}</p>
                            ) : locaisSessao.length === 0 ? (
                                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                                    <h3 className="text-lg font-medium text-muted-foreground">Nenhuma sala cadastrada</h3>
                                    <p className="text-sm text-muted-foreground mt-1 mb-4">Adicione locais específicos para esta sessão.</p>
                                    <Button onClick={() => { setLocalSessaoSelecionado(null); setShowForm(true); }}>
                                        <PlusCircle className="mr-2 h-4 w-4" /> Adicionar Nova Sala
                                    </Button>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left">
                                            <tr className="border-b">
                                                <th className="p-2 font-medium">Nome da Sala</th><th className="p-2 font-medium hidden sm:table-cell">Código da Sala</th><th className="p-2 font-medium text-right">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {locaisSessao.map(local => (
                                                <tr key={local.id} className="border-b last:border-b-0 hover:bg-accent/50 even:bg-muted/50 transition-colors">
                                                    <td className="p-2 font-medium">{local.nomeCompleto}</td>
                                                    <td className="p-2 text-muted-foreground hidden sm:table-cell">{local.codigoSala || 'N/A'}</td>
                                                    <td className="p-2 text-right space-x-2 whitespace-nowrap">
                                                        <Button variant="outline" size="sm" onClick={() => handleViewQrCode(local)}><QrCode className="h-4 w-4" /></Button>
                                                        <Button variant="outline" size="sm" onClick={() => handleEdit(local)}><Edit className="h-4 w-4" /></Button>
                                                        <Button variant="destructive" size="sm" onClick={() => handleDelete(local.id, local.nomeCompleto)}><Trash2 className="h-4 w-4" /></Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
                {!showForm && (
                    <CardFooter className="flex flex-col sm:flex-row justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:flex-1">
                            Fechar
                        </Button>
                    </CardFooter>
                )}
            </Card>

            {selectedLocalSessaoForQrCode && (
                <LocalSessaoQrCodeModal
                    isOpen={showQrCodeModal}
                    onClose={handleCloseQrCodeModal}
                    localSessao={selectedLocalSessaoForQrCode} // PASSANDO O OBJETO COMPLETO
                />
            )}

            {showImportModal && (
                <ImportLocaisSessaoModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    sessaoDestinoId={sessaoId}
                    sessaoDestinoNome={sessaoNome}
                    onSuccess={handleImportSuccess}
                />
            )}
        </div>
    );
}

export default GerenciarLocaisSessaoModal;